import { getRuntimeGlobalState } from "./conversationRuntimeService";
const TITLE_GENERATION_MODEL = "anthropic/claude-haiku-4-5";
const TITLE_SYSTEM_PROMPT = `You generate concise conversation titles.

Rules:
- 3-6 words
- Capture the main task/topic
- Return only the title text
- No trailing punctuation`;
function getGlobalState() {
    return getRuntimeGlobalState();
}
function toTitleCase(value) {
    return value
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}
function heuristicTitle(firstUserMessage) {
    const compact = firstUserMessage.replace(/\s+/g, " ").trim();
    if (!compact) {
        return "New Conversation";
    }
    const words = compact.split(" ").slice(0, 6);
    return toTitleCase(words.join(" "));
}
function sanitizeTitle(raw) {
    const singleLine = raw.replace(/\s+/g, " ").trim();
    const withoutQuotes = singleLine.replace(/^["']|["']$/g, "");
    const withoutLabel = withoutQuotes.replace(/^title\s*:\s*/i, "");
    const withoutTrailingPunctuation = withoutLabel.replace(/[.,;:!?]+$/, "").trim();
    if (!withoutTrailingPunctuation) {
        return "";
    }
    const words = withoutTrailingPunctuation.split(" ").slice(0, 6);
    return toTitleCase(words.join(" "));
}
export async function generateConversationTitle(firstUserMessage) {
    try {
        const globalState = getGlobalState();
        const llmService = globalState.getLLMService();
        if (!llmService) {
            return heuristicTitle(firstUserMessage);
        }
        const response = await llmService.createLLMResponseComplete([
            {
                role: "user",
                content: `Generate a title for this conversation start:\n\n${firstUserMessage}`,
            },
        ], TITLE_GENERATION_MODEL, {
            system: TITLE_SYSTEM_PROMPT,
            max_tokens: 40,
            temperature: 0.2,
        });
        const content = response?.choices?.[0]?.message?.content;
        const text = typeof content === "string"
            ? content
            : Array.isArray(content)
                ? content.map((part) => part?.text || "").join(" ")
                : "";
        const title = sanitizeTitle(text);
        return title || heuristicTitle(firstUserMessage);
    }
    catch (error) {
        console.error("[TitleGenerator] Failed to generate title:", error);
        return heuristicTitle(firstUserMessage);
    }
}
export async function generateAndPersistTitle(conversationId, firstUserMessage, onTitleGenerated) {
    try {
        const title = await generateConversationTitle(firstUserMessage);
        if (!title) {
            return null;
        }
        const globalState = getGlobalState();
        await globalState.persistConversation(conversationId, {
            title,
            status: "active",
        });
        onTitleGenerated?.(conversationId, title);
        return title;
    }
    catch (error) {
        console.error("[TitleGenerator] Failed to generate and persist title:", error);
        return null;
    }
}
